import { Button, ButtonSizesEnum, ButtonThemesEnum } from '@propertyfinder/button';
import { Checkbox } from '@propertyfinder/checkbox';
import { IconSpriteTimes } from '@propertyfinder/icons';
import { FunctionalComponent, h } from 'preact';

import { AgentModel } from 'common/data/agent/model';
import { BrokerModel } from 'common/data/broker/model';
import { PropertyModel } from 'common/data/property/model';
import { configGetCountry } from 'common/helper/config/get-country';
import { i18nTranslate } from 'common/helper/i18n/translate';
import { GoogleRecaptchaTemplate } from 'common/module/google/recaptcha/template';
import { LeadEmailFormInputInterface } from 'common/module/lead/email/form/input.interface';
import { LeadEmailFormTemplatePropsInterface } from 'common/module/lead/email/form/template-props.interface';
import { LeadEmailSignInComponent } from 'common/module/lead/email/sign-in/component';
import { MortgageComponent } from 'common/module/mortgage/component';
import { preactLifecycleComponent } from 'common/module/preact/lifecycle-component';
import { TelephoneInputComponent } from 'common/module/telephone-input/component';
import { LeadTemplateInfoAgent } from 'desktop/module/lead/template/info-agent';
import { LeadTemplateInfoBroker } from 'desktop/module/lead/template/info-broker';
import { LeadTemplateInfoProperty } from 'desktop/module/lead/template/info-property';

const Markup: FunctionalComponent<LeadEmailFormTemplatePropsInterface> = (props): preact.JSX.Element => {
  /**
   * Render specific error message
   */
  function renderError(propName: keyof LeadEmailFormInputInterface): preact.JSX.Element | null {
    if (props.input[propName] && props.input[propName].error) {
      return <div className='input__message input__message--invalid'>{props.input[propName].error}</div>;
    }

    return null;
  }

  /**
   * Render accept terms error
   */
  function renderAcceptConditionsError(): preact.JSX.Element | null {
    if (
      (props.input.acceptTerms && props.input.acceptTerms.error) ||
      (props.input.receiveAdvertising && props.input.receiveAdvertising.error)
    ) {
      return (
        <div className='input__message input__message--invalid contact-form__terms-error'>
          {i18nTranslate('Please accept the following conditions:')}
        </div>
      );
    }

    return null;
  }

  /**
   * Render general error message
   */
  function renderGeneralError(): preact.JSX.Element | null {
    if (props.error) {
      return <div className='contact-form__error--general'>{props.error}</div>;
    }

    return null;
  }

  /**
   * Render model information
   */
  function renderModelInfo(): preact.JSX.Element | null {
    if (props.model instanceof AgentModel) {
      return <LeadTemplateInfoAgent agent={props.model} />;
    } else if (props.model instanceof BrokerModel) {
      return <LeadTemplateInfoBroker broker={props.model} />;
    } else if (props.model instanceof PropertyModel) {
      return <LeadTemplateInfoProperty property={props.model} />;
    }

    return null;
  }

  /**
   * Render the form
   */
  function renderForm(): preact.JSX.Element {
    return (
      <form onSubmit={props.onSubmit} className='contact-form__form contact-form__form--big'>
        <div className='contact-form__input-container contact-form__text-area'>
          <textarea
            className={`input contact-form__area ${props.input.message.error ? 'input--invalid' : ''}`}
            onChange={(e: Event) => {
              props.onChange(e, 'message');
            }}
            onBlur={(e: Event) => {
              props.onBlur(e, 'message');
            }}
            placeholder={props.input.message.placeholder}
            value={props.input.message.value}
          />
          {renderError('message')}
        </div>
        {props.input.enquiryType && (
          <div className='contact-form__input-container'>
            <select
              className={`input input__select contact-form__select ${
                props.input.enquiryType.error ? 'input--invalid' : ''
              }`}
              value={props.input.enquiryType.value}
              onChange={(e: Event) => {
                props.onChange(e, 'enquiryType');
              }}
              onBlur={(e: Event) => {
                props.onBlur(e, 'enquiryType');
              }}
            >
              {props.input.enquiryType &&
                props.input.enquiryType.options.map((option) => <option value={option.value}>{option.text}</option>)}
            </select>
            {renderError('enquiryType')}
          </div>
        )}
        <div className='contact-form__user-info'>
          <div className='contact-form__input-container contact-form__name'>
            <input
              className={`input ${props.input.name.error ? 'input--invalid' : ''}`}
              placeholder={props.input.name.placeholder}
              value={props.input.name.value}
              name='name'
              onChange={(e: Event) => {
                props.onChange(e, 'name');
              }}
              onBlur={(e: Event) => {
                props.onBlur(e, 'name');
              }}
            />
            {renderError('name')}
          </div>
          <div className='contact-form__input-container contact-form__email'>
            <input
              name='email'
              className={`input ${props.input.email.error ? ' input--invalid' : ''}`}
              placeholder={props.input.email.placeholder}
              value={props.input.email.value}
              onChange={(e: Event) => {
                props.onChange(e, 'email');
              }}
              onBlur={(e: Event) => {
                props.onBlur(e, 'email');
              }}
            />
            {renderError('email')}
          </div>
        </div>
        <div className='contact-form__input-container'>
          <TelephoneInputComponent
            isLoading={!props.isCountriesListLoaded}
            hasError={props.input.phone.error}
            placeholder={props.input.phone.placeholder}
            name='phone'
            value={props.input.phone.value}
            countriesList={props.countriesList}
            selectedCountry={props.input.phone.countryCode}
            onChange={(e: Event) => {
              props.onChange(e, 'phone');
            }}
            onClickChangeCountryCode={props.onClickChangeCountryCode}
            onBlur={(e: Event) => {
              props.onBlur(e, 'phone');
            }}
          />
          {renderError('phone')}
        </div>

        <div className='contact-form__input-container contact-form__captcha'>
          <GoogleRecaptchaTemplate {...props.captcha} />
          {renderError('captcha')}
        </div>

        {props.input.acceptTerms || props.input.receiveAdvertising ? (
          <div>
            {renderAcceptConditionsError()}
            {props.input.acceptTerms && (
              <div className='contact-form__terms-area'>
                <Checkbox
                  id='acceptTerms'
                  name='acceptTerms'
                  checked={props.input.acceptTerms.value}
                  onClick={(e: MouseEvent): void => {
                    props.onCheck(e, 'acceptTerms');
                  }}
                >
                  {i18nTranslate('I have read and I accept the ')}
                  <a href={configGetCountry().termsUrl} target='_blank' className='link' rel='noreferrer'>
                    {i18nTranslate('general conditions of use ')}
                  </a>
                  {i18nTranslate(', in particular, the mention relating to the protection of personal data')}
                </Checkbox>
              </div>
            )}

            {props.input.receiveAdvertising && (
              <div className='contact-form__terms-area'>
                <Checkbox
                  id='receiveAdvertising'
                  name='receiveAdvertising'
                  checked={props.input.receiveAdvertising.value}
                  onClick={(e: MouseEvent): void => {
                    props.onCheck(e, 'receiveAdvertising');
                  }}
                >
                  {i18nTranslate('I have read and I accept to receive advertising messages')}
                </Checkbox>
              </div>
            )}
          </div>
        ) : null}

        <div className='contact-form__button-area'>
          {props.input.createAlert && (
            <Checkbox
              id='createAlert'
              name='createAlert'
              checked={props.input.createAlert.value}
              onClick={(e: MouseEvent): void => {
                props.onCheck(e, 'createAlert');
              }}
            >
              {props.input.createAlert.label}
            </Checkbox>
          )}
          <Button
            type='submit'
            theme={ButtonThemesEnum.PRIMARY}
            size={ButtonSizesEnum.DEFAULT}
            loading={props.status.submitting}
          >
            {i18nTranslate('Send')}
          </Button>
        </div>
      </form>
    );
  }

  /**
   * Render success message
   */
  function renderSuccessMessage(): preact.JSX.Element {
    return (
      <div className='success-message'>
        <span
          className='svg'
          dangerouslySetInnerHTML={{
            __html: `
            <svg viewBox='0 0 32 32' class='success-message__icon'>
              <use xlink:href='#svg-success-icon'></use>
            </svg>
          `,
          }}
        />
        <div className='success-message__text'>
          <span>{i18nTranslate('Thank you')}</span>
          <span>{i18nTranslate('Your message is on its way')}</span>
        </div>
        {props.isSignInComponentVisible && <LeadEmailSignInComponent />}
      </div>
    );
  }

  /**
   * Render mortgage campaign slot
   */
  function renderMortgageCampaign(): preact.JSX.Element {
    if (!(props.model instanceof PropertyModel) || !props.mortgageCampaignId) {
      return null;
    }

    return (
      <div className={`${props.status.submitted ? '' : 'contact-form--hidden'}`}>
        <MortgageComponent campaignId={props.mortgageCampaignId} model={props.model} loanConfig={undefined} />
      </div>
    );
  }

  return (
    <div className='contact-form'>
      <div className='modal_closebutton' onClick={props.onClose}>
        <IconSpriteTimes />
      </div>
      <div className='contact-form__header'>
        <div className='contact-form__heading'>{props.formTitle}</div>
      </div>
      {renderGeneralError()}
      {props.status.submitted && renderSuccessMessage()}
      {!props.isSignInComponentVisible && renderMortgageCampaign()}
      {!props.status.submitted && renderModelInfo()}
      {!props.status.submitted && renderForm()}
    </div>
  );
};

export const LeadEmailFormTemplate = preactLifecycleComponent(Markup);
